import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { PageHeader, Panel, EmptyState, fmtDate } from "@/components/shared/DashKit";
import { useContent } from "@/context/ContentContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowUpRight, ArrowDownLeft } from "lucide-react";

const TYPES = { DEPOSIT: "Dépôt", ORDER: "Commande", REFUND: "Remboursement", ADMIN_ADJUSTMENT: "Ajustement", ADJUSTMENT: "Ajustement", BONUS: "Bonus" };
const METHODS = { card: "Carte", mobile_money: "Mobile Money", usdt: "USDT TRC20" };

export default function Transactions() {
  const { t: tr } = useContent();
  const TYPE_LABELS = { DEPOSIT: tr("client.txn.type.deposit", "Dépôt"), ORDER: tr("client.txn.type.order", "Commande"), REFUND: tr("client.txn.type.refund", "Remboursement"), ADMIN_ADJUSTMENT: tr("client.txn.type.adjustment", "Ajustement"), ADJUSTMENT: tr("client.txn.type.adjustment", "Ajustement"), BONUS: tr("client.txn.type.bonus", "Bonus") };
  const METHOD_LABELS = { card: tr("client.txn.method.card", "Carte"), mobile_money: tr("client.txn.method.mm", "Mobile Money"), usdt: tr("client.txn.method.usdt", "USDT TRC20") };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");

  useEffect(() => {
    setLoading(true);
    const p = type ? `?type=${type}` : "";
    api.get(`/transactions${p}`).then((r) => { setItems(r.data); setLoading(false); });
  }, [type]);

  return (
    <div>
      <PageHeader title={tr("wallet.txn.title", "Transactions")} subtitle={tr("wallet.txn.subtitle", "Historique complet des mouvements de votre portefeuille.")} />
      <div className="mb-4 flex justify-end">
        <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="txn-type-filter" className="w-48"><SelectValue placeholder={tr("client.txn.filter.type", "Type")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{tr("client.txn.filter.all", "Tous les types")}</SelectItem>
            {Object.keys(TYPES).map((v) => <SelectItem key={v} value={v}>{TYPE_LABELS[v] || v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Panel>
        {loading ? <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>
          : items.length === 0 ? <EmptyState title={tr("wallet.txn.empty.title", "Aucune transaction")} subtitle={tr("wallet.txn.empty.subtitle", "Vos transactions apparaîtront ici.")} />
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left text-xs text-muted-foreground">
                  {[tr("client.txn.col.date", "Date"), tr("client.txn.col.type", "Type"), tr("client.txn.col.method", "Méthode"), tr("client.txn.col.reference", "Référence"), tr("client.txn.col.amount", "Montant"), tr("client.txn.col.balance", "Solde"), tr("client.txn.col.status", "Statut")].map((h, i) => <th key={i} className="pb-2 font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {items.map((t) => (
                    <tr key={t.id} className="border-b border-border/60 last:border-0" data-testid={`txn-row-${t.id}`}>
                      <td className="py-3 text-muted-foreground">{fmtDate(t.created_at)}</td>
                      <td className="py-3"><span className="inline-flex items-center gap-1.5 font-medium">{t.amount >= 0 ? <ArrowDownLeft className="h-3.5 w-3.5 text-emerald-500" /> : <ArrowUpRight className="h-3.5 w-3.5 text-red-500" />}{TYPE_LABELS[t.type] || t.type}</span></td>
                      <td className="py-3 text-muted-foreground">{METHOD_LABELS[t.payment_method] || "—"}</td>
                      <td className="py-3 font-mono text-xs text-muted-foreground">{t.reference}</td>
                      <td className={`py-3 font-medium ${t.amount >= 0 ? "text-emerald-600" : "text-red-600"}`}>{t.amount >= 0 ? "+" : ""}{money(t.amount)}</td>
                      <td className="py-3">{money(t.balance_after)}</td>
                      <td className="py-3 text-muted-foreground capitalize">{t.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Panel>
    </div>
  );
}
